import React, { Suspense, lazy, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { EditModeProvider } from './components/EditModeProvider';
import Header from './components/Header';
import Footer from './components/Footer';
import { Toaster } from './components/ui/toaster';
// Cookie consent is handled by Iubenda (script in public/index.html). The
// custom CookieBanner was removed so it doesn't overwrite the Iubenda global
// helper at import time.

// Public
const Home = lazy(() => import('./pages/Home'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Stakeholders = lazy(() => import('./pages/Stakeholders'));
const WhatWeDo = lazy(() => import('./pages/WhatWeDo'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Careers = lazy(() => import('./pages/Careers'));
const SearchPage = lazy(() => import('./pages/Search'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Admin
const AdminLogin = lazy(() => import('./admin/AdminLogin'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const AdminContent = lazy(() => import('./admin/AdminContent'));
const AdminMedia = lazy(() => import('./admin/AdminMedia'));
const AdminMessages = lazy(() => import('./admin/AdminMessages'));
const AdminAudit = lazy(() => import('./admin/AdminAudit'));
const AdminSeo = lazy(() => import('./admin/AdminSeo'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[70vh]">
    <div className="w-10 h-10 border-4 border-[#22C55E]/20 border-t-[#22C55E] rounded-full animate-spin" />
  </div>
);

// Cross-route hash scroll: React Router v6 does NOT scroll to anchors declared
// via `<Link to="/foo#bar">`. When the user clicks a submenu item like
// "Chi siamo → Vision" (href="/about-us#vision") we need to (a) let the route
// mount + lazy-loaded chunk render, then (b) find the element with matching id
// and scroll it into view. Multiple retries are needed because content below
// the fold is lazy-rendered and the target section may not be in the DOM yet
// on the first tick.
const ScrollToHash = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let attempts = 0;
    const maxAttempts = 60; // 60 * 100ms = 6s — Home + heavy sections may lazy-load slowly on cold cache
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        // 'auto' (instant) is more reliable when the user is coming from
        // /search and expects to land exactly on the section. Smooth scroll
        // could be interrupted by parallel DOM mutations from lazy sections
        // still mounting below.
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
      if (++attempts < maxAttempts) setTimeout(tryScroll, 100);
    };
    tryScroll();
  }, [pathname, hash]);
  return null;
};

const PublicShell = ({ children }) => (
  <LanguageProvider>
    <EditModeProvider>
      <div className="App min-h-screen flex flex-col bg-white">
        <Header />
        <div className="flex-1">{children}</div>
        <Footer />
        {/* Cookie consent is handled by Iubenda (script in index.html). The custom
            CookieBanner remains available for local dev fallback but is not rendered. */}
      </div>
    </EditModeProvider>
  </LanguageProvider>
);

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToHash />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Admin routes (no public header/footer) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="media" element={<AdminMedia />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="seo" element={<AdminSeo />} />
          </Route>

          {/* Public site */}
          <Route path="/*" element={
            <PublicShell>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/stakeholders" element={<Stakeholders />} />
                <Route path="/what-we-do" element={<WhatWeDo />} />
                <Route path="/what-we-do/:slug" element={<WhatWeDo />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/search" element={<SearchPage />} />
                {/* Legacy WP URLs → new SPA routes (client-side 301 equivalent).
                    Ingress-level redirects for /wp-content/uploads/* and non-SPA
                    paths are documented in /app/memory/URL_MIGRATION_MAP.md */}
                <Route path="/chi-siamo" element={<Navigate to="/about-us" replace />} />
                <Route path="/chi-siamo/*" element={<Navigate to="/about-us" replace />} />
                <Route path="/consulenza" element={<Navigate to="/what-we-do/consulting" replace />} />
                <Route path="/consulenza/*" element={<Navigate to="/what-we-do/consulting" replace />} />
                <Route path="/media-publishing" element={<Navigate to="/what-we-do/media-publishing" replace />} />
                <Route path="/media-publishing/*" element={<Navigate to="/what-we-do/media-publishing" replace />} />
                <Route path="/energia-pulita" element={<Navigate to="/what-we-do/clean-energy" replace />} />
                <Route path="/energia-pulita/*" element={<Navigate to="/what-we-do/clean-energy" replace />} />
                <Route path="/progetti-ambientali" element={<Navigate to="/what-we-do/environmental-projects" replace />} />
                <Route path="/progetti-ambientali/*" element={<Navigate to="/what-we-do/environmental-projects" replace />} />
                <Route path="/tecnologia-ed-efficientamento" element={<Navigate to="/what-we-do/environmental-projects" replace />} />
                <Route path="/tecnologia-ed-efficientamento/*" element={<Navigate to="/what-we-do/environmental-projects" replace />} />
                <Route path="/stakeholder" element={<Navigate to="/stakeholders" replace />} />
                <Route path="/stakeholder/*" element={<Navigate to="/stakeholders" replace />} />
                <Route path="/stakeholder-2" element={<Navigate to="/stakeholders" replace />} />
                <Route path="/stakeholder-2/*" element={<Navigate to="/stakeholders" replace />} />
                <Route path="/contatti" element={<Navigate to="/contacts" replace />} />
                <Route path="/contatti/*" element={<Navigate to="/contacts" replace />} />
                <Route path="/sostenibilita" element={<Navigate to="/#manifesto" replace />} />
                <Route path="/sostenibilita/*" element={<Navigate to="/#manifesto" replace />} />
                {/* Malformed dup-domain paths (e.g. /company.lifegate.it/consulenza/) */}
                <Route path="/company.lifegate.it/*" element={<Navigate to="/" replace />} />
                {/* Catch-all 404 — must be the LAST route so all previously matched
                    paths take priority. Returns a friendly localized "not found" page
                    with header/footer + noindex meta so Google won't treat unknown URLs
                    as soft-404s. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PublicShell>
          } />
        </Routes>
      </Suspense>
      <Toaster />
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
